import { createAction } from "@reduxjs/toolkit";
import { createRsaaAction } from "../../components/utilities/ReduxUtils";

// Action types
export const LOAD_NOTEBOOKS = "LOAD_NOTEBOOKS";
export const CREATE_NOTEBOOK = "CREATE_NOTEBOOK";
export const UPDATE_NOTEBOOK = "UPDATE_NOTEBOOK";
export const DELETE_NOTEBOOK = "DELETE_NOTEBOOK";
export const SET_CURRENT_NOTEBOOK = "SET_CURRENT_NOTEBOOK";
export const LOAD_NOTES = "LOAD_NOTES";
export const LOAD_NOTE_DETAILS = "LOAD_NOTE_DETAILS";
export const CREATE_NOTE = "CREATE_NOTE";
export const UPDATE_NOTE = "UPDATE_NOTE";
export const DELETE_NOTE = "DELETE_NOTE";
export const CREATE_NOTE_BLOCK = "CREATE_NOTE_BLOCK";
export const UPDATE_NOTE_BLOCK = "UPDATE_NOTE_BLOCK";
export const DELETE_NOTE_BLOCK = "DELETE_NOTE_BLOCK";
export const REORDER_BLOCKS = "REORDER_BLOCKS";

// Interfaces
export interface INotebook {
    ui_id: string;
    name: string;
    description?: string;
    icon?: string;
    note_count?: number;
}

export interface INote {
    ui_id: string;
    notebook_ui_id: string;
    name: string;
    description?: string;
    blocks: INoteBlock[];
    block_count?: number;
}

export interface INoteBlock {
    ui_id: string;
    type: "markdown" | "chemical" | "reaction" | "multi-step-reaction";
    position: number;
    text_content?: string;
    chemical_ui_id?: string;
    reaction_ui_id?: string;
    reaction_route_ui_id?: string;
    // Legacy compatibility field retained for old persisted clients.
    multi_step_reaction_ui_id?: string;
}

export interface ICreateNotebookRequest {
    name: string;
    description?: string;
    icon?: string;
}

export interface ICreateNoteRequest {
    notebook_ui_id: string;
    name: string;
    description?: string;
}

export interface ICreateNoteBlockRequest {
    type: "markdown" | "chemical" | "reaction" | "multi-step-reaction";
    position: number;
    text_content?: string;
    chemical_ui_id?: string;
    reaction_ui_id?: string;
    reaction_route_ui_id?: string;
    // Legacy compatibility field retained for old persisted clients.
    multi_step_reaction_ui_id?: string;
}

export interface IReorderBlocksRequest {
    blocks: Array<{
        block_ui_id: string;
        position: number;
    }>;
}

export interface IReorderBlocksResponse {
    blocks: Array<{
        block_ui_id: string;
        position: number;
    }>;
}

// Notebook actions
export const loadNotebooks = createRsaaAction<void, INotebook[]>({
    path: "notebooks/notebooks",
    method: "GET",
    type: LOAD_NOTEBOOKS,
});

export const createNotebook = createRsaaAction<
    ICreateNotebookRequest,
    INotebook
>({
    path: "notebooks/notebooks",
    method: "POST",
    type: CREATE_NOTEBOOK,
});

export const updateNotebook = createRsaaAction<INotebook, INotebook>({
    path: "notebooks/notebooks/:id",
    method: "PUT",
    type: UPDATE_NOTEBOOK,
});

export const deleteNotebook = createRsaaAction<void, void>({
    path: "notebooks/notebooks/:id",
    method: "DELETE",
    type: DELETE_NOTEBOOK,
});

// Note actions
export const loadNotes = createRsaaAction<void, INote[]>({
    path: "notebooks/:notebookId/notes",
    method: "GET",
    type: LOAD_NOTES,
});

export const loadNoteDetails = createRsaaAction<void, INote>({
    path: "notebooks/:notebookId/notes/:noteId",
    method: "GET",
    type: LOAD_NOTE_DETAILS,
});

export const createNote = createRsaaAction<ICreateNoteRequest, INote>({
    path: "notebooks/:notebookId/notes",
    method: "POST",
    type: CREATE_NOTE,
});

export const updateNote = createRsaaAction<INote, INote>({
    path: "notebooks/:notebookId/notes/:id",
    method: "PUT",
    type: UPDATE_NOTE,
});

export const deleteNote = createRsaaAction<void, void>({
    path: "notebooks/:notebookId/notes/:id",
    method: "DELETE",
    type: DELETE_NOTE,
});

// Note block actions
export const createNoteBlock = createRsaaAction<
    ICreateNoteBlockRequest,
    INoteBlock
>({
    path: "notebooks/:notebookId/notes/:noteId/blocks",
    method: "POST",
    type: CREATE_NOTE_BLOCK,
});

export const updateNoteBlock = createRsaaAction<INoteBlock, INoteBlock>({
    path: "notebooks/:notebookId/notes/:noteId/blocks/:blockId",
    method: "PUT",
    type: UPDATE_NOTE_BLOCK,
});

export const deleteNoteBlock = createRsaaAction<void, void>({
    path: "notebooks/:notebookId/notes/:noteId/blocks/:blockId",
    method: "DELETE",
    type: DELETE_NOTE_BLOCK,
});

export const reorderBlocks = createRsaaAction<
    IReorderBlocksRequest,
    IReorderBlocksResponse
>({
    path: "notebooks/:notebookId/notes/:noteId",
    method: "PATCH",
    type: REORDER_BLOCKS,
});

// Local actions
export const setCurrentNotebook = createAction<string | null>(
    SET_CURRENT_NOTEBOOK
);
